import * as THREE from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import {
  cameraPosition,
  positionWorld,
  texture as textureNode,
  uniform,
  wgslFn,
} from 'three/tsl';
import { taauMipBiasUniform } from './TaauMipBias';
import type { RendererMode } from './Renderer';

/**
 * Stylised sunset sky for the Twin Suns Autodrome.
 *
 * A backside-rendered sphere whose fragment shader paints a 3-stop vertical
 * gradient (deep violet zenith → magenta band → warm orange horizon) plus two
 * soft sun discs. The sphere follows the camera so the gradient is anchored
 * to the world up-axis regardless of where the player is on the track.
 *
 * The sky exposes its horizon colour so the scene fog can be bound to the
 * same value — that's what dissolves rocks/mesas into the painted backdrop
 * instead of fading them into a flat clear-colour mismatch.
 */

export interface GradientSky {
  mesh: THREE.Mesh;
  horizonColor: THREE.Color;
  uniforms: {
    uHorizonColor: { value: THREE.Color };
    uLowerColor: { value: THREE.Color };
    uMidColor: { value: THREE.Color };
    uUpperColor: { value: THREE.Color };
    uZenithColor: { value: THREE.Color };
    uOppositeHorizonColor: { value: THREE.Color };
    uOppositeHorizonStrength: { value: number };
    uHorizonGlowColor: { value: THREE.Color };
    uHorizonGlowIntensity: { value: number };
    uSunColor: { value: THREE.Color };
    uSecondarySunColor: { value: THREE.Color };
    uHorizonHueShift: { value: number };
    uSunDiscScale: { value: number };
    uSunHaloScale: { value: number };
    uSecondarySunDiscScale: { value: number };
    uSecondarySunHaloScale: { value: number };
    uStarIntensity: { value: number };
    uSunDir: { value: THREE.Vector3 };
    uSecondarySunDir: { value: THREE.Vector3 };
  };
  follow: (cameraPosition: THREE.Vector3) => void;
  applyStyle: (style: {
    horizonHex: string;
    lowerHex?: string;
    midHex: string;
    upperHex?: string;
    zenithHex: string;
    oppositeHorizonHex?: string;
    oppositeHorizonStrength?: number;
    horizonGlowHex?: string;
    horizonGlowIntensity?: number;
    sunDiscScale?: number;
    sunHaloScale?: number;
    secondarySunDiscScale?: number;
    secondarySunHaloScale?: number;
    starIntensity?: number;
    sunDir: { x: number; y: number; z: number };
    secondarySunDir: { x: number; y: number; z: number };
    sunColorHex?: string;
    secondarySunColorHex?: string;
    horizonHueShift?: number;
  }) => void;
}

export interface PanoramaSky {
  mesh: THREE.Mesh;
  horizonColor: THREE.Color;
  follow: (cameraPosition: THREE.Vector3) => void;
}

export interface PanoramaSkyOptions {
  seamFeather?: number;
  seamOpacity?: number;
  rotationY?: number;
  rendererMode?: RendererMode;
}

const makeNodeUniform = uniform as any;
const makeWgslFn = wgslFn as any;

const VERTEX_SHADER = /* glsl */ `
  varying vec3 vWorldPos;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

const FRAGMENT_SHADER = /* glsl */ `
  uniform vec3 uHorizonColor;
  uniform vec3 uLowerColor;
  uniform vec3 uMidColor;
  uniform vec3 uUpperColor;
  uniform vec3 uZenithColor;
  uniform vec3 uOppositeHorizonColor;
  uniform float uOppositeHorizonStrength;
  uniform vec3 uHorizonGlowColor;
  uniform float uHorizonGlowIntensity;
  uniform vec3 uSunColor;
  uniform vec3 uSecondarySunColor;
  uniform float uHorizonHueShift;
  uniform float uSunDiscScale;
  uniform float uSunHaloScale;
  uniform float uSecondarySunDiscScale;
  uniform float uSecondarySunHaloScale;
  uniform float uStarIntensity;
  uniform vec3 uSunDir;
  uniform vec3 uSecondarySunDir;
  varying vec3 vWorldPos;

  float hashSky(vec2 p) {
    p = fract(p * vec2(127.1, 311.7));
    p += dot(p, p + 19.19);
    return fract(p.x * p.y);
  }

  void main() {
    vec3 dir = normalize(vWorldPos - cameraPosition);
    float h = dir.y;

    float aboveHorizon = smoothstep(-0.12, 0.08, h);
    float lowerBand = smoothstep(-0.06, 0.24, h);
    float midBand = smoothstep(0.12, 0.58, h);
    float upperBand = smoothstep(0.42, 0.84, h);
    float zenithBand = pow(smoothstep(0.62, 1.0, h), 1.2);

    vec3 col = mix(uHorizonColor, uLowerColor, lowerBand);
    col = mix(col, uMidColor, midBand);
    col = mix(col, uUpperColor, upperBand);
    col = mix(col, uZenithColor, zenithBand);
    col = mix(uHorizonColor, col, aboveHorizon);
    float horizonTintMask = 1.0 - smoothstep(0.02, 0.48, abs(h));
    vec3 warmTint = vec3(1.16, 0.96, 0.78);
    vec3 coolTint = vec3(0.78, 0.94, 1.18);
    vec3 hueTint = mix(coolTint, warmTint, clamp(uHorizonHueShift * 0.5 + 0.5, 0.0, 1.0));
    col = mix(col, col * hueTint, horizonTintMask * abs(uHorizonHueShift));

    // Twin suns: tight bright core + soft halo.
    vec3 sunDir = normalize(uSunDir);
    vec3 secondarySunDir = normalize(uSecondarySunDir);
    float d1 = max(0.0, dot(dir, sunDir));
    float d2 = max(0.0, dot(dir, secondarySunDir));
    float core1 = smoothstep(1.0 - 0.0015 * max(0.2, uSunDiscScale), 0.9998, d1);
    float halo1 = smoothstep(1.0 - 0.080 * max(0.2, uSunHaloScale), 0.998, d1) * 0.35;
    float core2 = smoothstep(1.0 - 0.0010 * max(0.2, uSecondarySunDiscScale), 0.9999, d2);
    float halo2 = smoothstep(1.0 - 0.050 * max(0.2, uSecondarySunHaloScale), 0.999, d2) * 0.22;
    col += uSunColor * (core1 + halo1);
    col += uSecondarySunColor * (core2 + halo2);

    vec2 flatDir = normalize(dir.xz + vec2(0.0001));
    vec2 flatSun = normalize(sunDir.xz + vec2(0.0001));
    vec2 flatOpposite = -flatSun;
    float horizonMask = (1.0 - smoothstep(0.02, 0.42, abs(h))) * smoothstep(-0.16, 0.12, h);
    float sunAzimuth = pow(max(0.0, dot(flatDir, flatSun)), 3.0);
    float oppositeAzimuth = pow(max(0.0, dot(flatDir, flatOpposite)), 2.2);
    col += uHorizonGlowColor * horizonMask * sunAzimuth * uHorizonGlowIntensity;
    col = mix(col, uOppositeHorizonColor, horizonMask * oppositeAzimuth * uOppositeHorizonStrength);

    float starSeed = hashSky(floor(dir.xz * 420.0 + dir.y * 57.0));
    float stars = step(0.996, starSeed) * smoothstep(0.18, 0.75, h) * uStarIntensity;
    col += vec3(stars);

    gl_FragColor = vec4(col, 1.0);
  }
`;

const PANORAMA_VERTEX_SHADER = /* glsl */ `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const PANORAMA_FRAGMENT_SHADER = /* glsl */ `
  uniform sampler2D uSkyMap;
  uniform float uSeamFeather;
  uniform float uSeamOpacity;
  varying vec2 vUv;

  void main() {
    vec4 col = texture2D(uSkyMap, vUv);
    float feather = max(uSeamFeather, 0.0001);

    // Soften generated-panorama edge mismatches by forcing both sides of the
    // UV split toward the same averaged seam colour. This keeps the treatment
    // local to the wrapped edge instead of tinting the whole sky.
    vec4 seamColor = 0.5 * (
      texture2D(uSkyMap, vec2(0.0, vUv.y)) +
      texture2D(uSkyMap, vec2(1.0, vUv.y))
    );
    float edgeDist = min(vUv.x, 1.0 - vUv.x);
    float seamMask = (1.0 - smoothstep(0.0, feather, edgeDist)) * uSeamOpacity;
    col = mix(col, seamColor, seamMask);

    gl_FragColor = col;
    #include <colorspace_fragment>
  }
`;

export function createGradientSky(rendererMode: RendererMode = 'webgl2'): GradientSky {
  const horizonColor = new THREE.Color(0x9de5ff);
  const lowerColor = new THREE.Color(0x55c9f4);
  const midColor = new THREE.Color(0x29aef2);
  const upperColor = new THREE.Color(0x1688d8);
  const zenithColor = new THREE.Color(0x0872c7);
  const oppositeHorizonColor = new THREE.Color(0x8be7ff);
  const horizonGlowColor = new THREE.Color(0xffd06f);
  const sunColor = new THREE.Color(0xffe7a3);
  const secondarySunColor = new THREE.Color(0x9ad7ff);
  const sunDir = new THREE.Vector3(72, 98, 48).normalize();
  const secondarySunDir = new THREE.Vector3(120, 70, 80).normalize();

  const uniforms = {
    uHorizonColor: { value: horizonColor },
    uLowerColor: { value: lowerColor },
    uMidColor: { value: midColor },
    uUpperColor: { value: upperColor },
    uZenithColor: { value: zenithColor },
    uOppositeHorizonColor: { value: oppositeHorizonColor },
    uOppositeHorizonStrength: { value: 0.22 },
    uHorizonGlowColor: { value: horizonGlowColor },
    uHorizonGlowIntensity: { value: 1.1 },
    uSunColor: { value: sunColor },
    uSecondarySunColor: { value: secondarySunColor },
    uHorizonHueShift: { value: 0 },
    uSunDiscScale: { value: 1 },
    uSunHaloScale: { value: 1 },
    uSecondarySunDiscScale: { value: 1 },
    uSecondarySunHaloScale: { value: 1 },
    uStarIntensity: { value: 0 },
    uSunDir: { value: sunDir },
    uSecondarySunDir: { value: secondarySunDir },
  };

  const webgpuMaterial = rendererMode === 'webgpu' ? makeGradientSkyNodeMaterial(uniforms) : null;
  const material = webgpuMaterial
    ?? new THREE.ShaderMaterial({
      uniforms,
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
    });

  // Radius is arbitrary — sphere follows camera and is rendered first with
  // depthWrite off, so any geometry between camera and sky writes over it.
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(800, 24, 16), material);
  mesh.name = 'gradient-sky';
  mesh.frustumCulled = false;
  mesh.renderOrder = -1000;

  return {
    mesh,
    horizonColor,
    uniforms,
    follow: (cameraPosition) => mesh.position.copy(cameraPosition),
    applyStyle: (style) => {
      horizonColor.set(style.horizonHex);
      uniforms.uHorizonColor.value.set(style.horizonHex);
      uniforms.uLowerColor.value.set(style.lowerHex ?? style.horizonHex);
      uniforms.uMidColor.value.set(style.midHex);
      uniforms.uUpperColor.value.set(style.upperHex ?? style.midHex);
      uniforms.uZenithColor.value.set(style.zenithHex);
      uniforms.uOppositeHorizonColor.value.set(style.oppositeHorizonHex ?? style.midHex);
      uniforms.uOppositeHorizonStrength.value = style.oppositeHorizonStrength ?? 0;
      uniforms.uHorizonGlowColor.value.set(style.horizonGlowHex ?? style.horizonHex);
      uniforms.uHorizonGlowIntensity.value = style.horizonGlowIntensity ?? 0;
      uniforms.uSunDiscScale.value = style.sunDiscScale ?? 1;
      uniforms.uSunHaloScale.value = style.sunHaloScale ?? 1;
      uniforms.uSecondarySunDiscScale.value = style.secondarySunDiscScale ?? 1;
      uniforms.uSecondarySunHaloScale.value = style.secondarySunHaloScale ?? 1;
      uniforms.uStarIntensity.value = style.starIntensity ?? 0;
      if (style.sunColorHex) uniforms.uSunColor.value.set(style.sunColorHex);
      if (style.secondarySunColorHex) uniforms.uSecondarySunColor.value.set(style.secondarySunColorHex);
      uniforms.uHorizonHueShift.value = style.horizonHueShift ?? 0;
      uniforms.uSunDir.value.set(style.sunDir.x, style.sunDir.y, style.sunDir.z).normalize();
      uniforms.uSecondarySunDir.value
        .set(style.secondarySunDir.x, style.secondarySunDir.y, style.secondarySunDir.z)
        .normalize();
      const webgpuUniforms = material.userData.webgpuSkyUniforms as WebGPUGradientSkyUniforms | undefined;
      if (webgpuUniforms) applyWebGPUGradientSkyUniforms(webgpuUniforms, uniforms);
    },
  };
}

export function createPanoramaSky(textureUrl: string, opts: PanoramaSkyOptions = {}): PanoramaSky {
  const horizonColor = new THREE.Color(0xd4815d);
  const loader = new THREE.TextureLoader();
  const texture = loader.load(textureUrl);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;

  let material: THREE.Material;
  if (opts.rendererMode === 'webgpu') {
    // NodeMaterial path: colorNode samples the panorama with the TAAU mip-bias
    // uniform so 0.7× upscale picks sharper mips instead of leaning on the
    // GPU's default selection (which assumes 1:1 pixel-to-texel sampling).
    const nodeMat = new MeshBasicNodeMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
      toneMapped: false,
    });
    (nodeMat as unknown as { colorNode: unknown }).colorNode =
      (textureNode as unknown as (t: THREE.Texture) => { bias: (u: unknown) => unknown })(texture)
        .bias(taauMipBiasUniform);
    material = nodeMat;
  } else {
    material = new THREE.ShaderMaterial({
      uniforms: {
        uSkyMap: { value: texture },
        uSeamFeather: { value: opts.seamFeather ?? 0.018 },
        uSeamOpacity: { value: opts.seamOpacity ?? 0.58 },
      },
      vertexShader: PANORAMA_VERTEX_SHADER,
      fragmentShader: PANORAMA_FRAGMENT_SHADER,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
      toneMapped: false,
    });
  }

  const mesh = new THREE.Mesh(new THREE.SphereGeometry(900, 64, 32), material);
  mesh.name = 'panorama-sky';
  mesh.rotation.y = opts.rotationY ?? Math.PI;
  mesh.frustumCulled = false;
  mesh.renderOrder = -1000;

  return {
    mesh,
    horizonColor,
    follow: (cameraPosition) => mesh.position.copy(cameraPosition),
  };
}

interface WebGPUGradientSkyUniforms {
  uHorizonColor: { value: THREE.Vector3 };
  uLowerColor: { value: THREE.Vector3 };
  uMidColor: { value: THREE.Vector3 };
  uUpperColor: { value: THREE.Vector3 };
  uZenithColor: { value: THREE.Vector3 };
  uOppositeHorizonColor: { value: THREE.Vector3 };
  uOppositeHorizonStrength: { value: number };
  uHorizonGlowColor: { value: THREE.Vector3 };
  uHorizonGlowIntensity: { value: number };
  uSunColor: { value: THREE.Vector3 };
  uSecondarySunColor: { value: THREE.Vector3 };
  uHorizonHueShift: { value: number };
  uSunDiscScale: { value: number };
  uSunHaloScale: { value: number };
  uSecondarySunDiscScale: { value: number };
  uSecondarySunHaloScale: { value: number };
  uStarIntensity: { value: number };
  uSunDir: { value: THREE.Vector3 };
  uSecondarySunDir: { value: THREE.Vector3 };
}

const skyHashNode = makeWgslFn(`
fn sky_hash(p: vec2<f32>) -> f32 {
  var q = fract(p * vec2<f32>(127.1, 311.7));
  q = q + dot(q, q + vec2<f32>(19.19, 19.19));
  return fract(q.x * q.y);
}
`);

const gradientSkyColorNode = makeWgslFn(`
fn gradient_sky_color(
  worldPosition: vec3<f32>,
  cameraWorldPosition: vec3<f32>,
  horizonColor: vec3<f32>,
  lowerColor: vec3<f32>,
  midColor: vec3<f32>,
  upperColor: vec3<f32>,
  zenithColor: vec3<f32>,
  oppositeHorizonColor: vec3<f32>,
  oppositeHorizonStrength: f32,
  horizonGlowColor: vec3<f32>,
  horizonGlowIntensity: f32,
  sunColor: vec3<f32>,
  secondarySunColor: vec3<f32>,
  horizonHueShift: f32,
  sunDiscScale: f32,
  sunHaloScale: f32,
  secondarySunDiscScale: f32,
  secondarySunHaloScale: f32,
  starIntensity: f32,
  sunDirInput: vec3<f32>,
  secondarySunDirInput: vec3<f32>
) -> vec3<f32> {
  let dir = normalize(worldPosition - cameraWorldPosition);
  let h = dir.y;

  let aboveHorizon = smoothstep(-0.12, 0.08, h);
  let lowerBand = smoothstep(-0.06, 0.24, h);
  let midBand = smoothstep(0.12, 0.58, h);
  let upperBand = smoothstep(0.42, 0.84, h);
  let zenithBand = pow(smoothstep(0.62, 1.0, h), 1.2);

  var col = mix(horizonColor, lowerColor, lowerBand);
  col = mix(col, midColor, midBand);
  col = mix(col, upperColor, upperBand);
  col = mix(col, zenithColor, zenithBand);
  col = mix(horizonColor, col, aboveHorizon);

  let horizonTintMask = 1.0 - smoothstep(0.02, 0.48, abs(h));
  let warmTint = vec3<f32>(1.16, 0.96, 0.78);
  let coolTint = vec3<f32>(0.78, 0.94, 1.18);
  let hueTint = mix(coolTint, warmTint, clamp(horizonHueShift * 0.5 + 0.5, 0.0, 1.0));
  col = mix(col, col * hueTint, horizonTintMask * abs(horizonHueShift));

  let sunDir = normalize(sunDirInput);
  let secondarySunDir = normalize(secondarySunDirInput);
  let d1 = max(0.0, dot(dir, sunDir));
  let d2 = max(0.0, dot(dir, secondarySunDir));
  let core1 = smoothstep(1.0 - 0.0015 * max(0.2, sunDiscScale), 0.9998, d1);
  let halo1 = smoothstep(1.0 - 0.080 * max(0.2, sunHaloScale), 0.998, d1) * 0.35;
  let core2 = smoothstep(1.0 - 0.0010 * max(0.2, secondarySunDiscScale), 0.9999, d2);
  let halo2 = smoothstep(1.0 - 0.050 * max(0.2, secondarySunHaloScale), 0.999, d2) * 0.22;
  col = col + sunColor * (core1 + halo1);
  col = col + secondarySunColor * (core2 + halo2);

  let flatDir = normalize(dir.xz + vec2<f32>(0.0001, 0.0001));
  let flatSun = normalize(sunDir.xz + vec2<f32>(0.0001, 0.0001));
  let flatOpposite = -flatSun;
  let horizonMask = (1.0 - smoothstep(0.02, 0.42, abs(h))) * smoothstep(-0.16, 0.12, h);
  let sunAzimuth = pow(max(0.0, dot(flatDir, flatSun)), 3.0);
  let oppositeAzimuth = pow(max(0.0, dot(flatDir, flatOpposite)), 2.2);
  col = col + horizonGlowColor * horizonMask * sunAzimuth * horizonGlowIntensity;
  col = mix(col, oppositeHorizonColor, horizonMask * oppositeAzimuth * oppositeHorizonStrength);

  let starSeed = sky_hash(floor(dir.xz * 420.0 + vec2<f32>(dir.y * 57.0, dir.y * 57.0)));
  let stars = step(0.996, starSeed) * smoothstep(0.18, 0.75, h) * starIntensity;
  return col + vec3<f32>(stars, stars, stars);
}
`, [skyHashNode]);

function makeGradientSkyNodeMaterial(uniforms: GradientSky['uniforms']): MeshBasicNodeMaterial {
  const webgpuUniforms = makeWebGPUGradientSkyUniforms(uniforms);
  const material = new MeshBasicNodeMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    toneMapped: false,
  });
  material.colorNode = gradientSkyColorNode({
    worldPosition: positionWorld,
    cameraWorldPosition: cameraPosition,
    horizonColor: webgpuUniforms.uHorizonColor,
    lowerColor: webgpuUniforms.uLowerColor,
    midColor: webgpuUniforms.uMidColor,
    upperColor: webgpuUniforms.uUpperColor,
    zenithColor: webgpuUniforms.uZenithColor,
    oppositeHorizonColor: webgpuUniforms.uOppositeHorizonColor,
    oppositeHorizonStrength: webgpuUniforms.uOppositeHorizonStrength,
    horizonGlowColor: webgpuUniforms.uHorizonGlowColor,
    horizonGlowIntensity: webgpuUniforms.uHorizonGlowIntensity,
    sunColor: webgpuUniforms.uSunColor,
    secondarySunColor: webgpuUniforms.uSecondarySunColor,
    horizonHueShift: webgpuUniforms.uHorizonHueShift,
    sunDiscScale: webgpuUniforms.uSunDiscScale,
    sunHaloScale: webgpuUniforms.uSunHaloScale,
    secondarySunDiscScale: webgpuUniforms.uSecondarySunDiscScale,
    secondarySunHaloScale: webgpuUniforms.uSecondarySunHaloScale,
    starIntensity: webgpuUniforms.uStarIntensity,
    sunDirInput: webgpuUniforms.uSunDir,
    secondarySunDirInput: webgpuUniforms.uSecondarySunDir,
  });
  material.userData.webgpuSkyUniforms = webgpuUniforms;
  return material;
}

function makeWebGPUGradientSkyUniforms(uniforms: GradientSky['uniforms']): WebGPUGradientSkyUniforms {
  return {
    uHorizonColor: makeNodeUniform(skyColorVector(uniforms.uHorizonColor.value)),
    uLowerColor: makeNodeUniform(skyColorVector(uniforms.uLowerColor.value)),
    uMidColor: makeNodeUniform(skyColorVector(uniforms.uMidColor.value)),
    uUpperColor: makeNodeUniform(skyColorVector(uniforms.uUpperColor.value)),
    uZenithColor: makeNodeUniform(skyColorVector(uniforms.uZenithColor.value)),
    uOppositeHorizonColor: makeNodeUniform(skyColorVector(uniforms.uOppositeHorizonColor.value)),
    uOppositeHorizonStrength: makeNodeUniform(uniforms.uOppositeHorizonStrength.value),
    uHorizonGlowColor: makeNodeUniform(skyColorVector(uniforms.uHorizonGlowColor.value)),
    uHorizonGlowIntensity: makeNodeUniform(uniforms.uHorizonGlowIntensity.value),
    uSunColor: makeNodeUniform(skyColorVector(uniforms.uSunColor.value)),
    uSecondarySunColor: makeNodeUniform(skyColorVector(uniforms.uSecondarySunColor.value)),
    uHorizonHueShift: makeNodeUniform(uniforms.uHorizonHueShift.value),
    uSunDiscScale: makeNodeUniform(uniforms.uSunDiscScale.value),
    uSunHaloScale: makeNodeUniform(uniforms.uSunHaloScale.value),
    uSecondarySunDiscScale: makeNodeUniform(uniforms.uSecondarySunDiscScale.value),
    uSecondarySunHaloScale: makeNodeUniform(uniforms.uSecondarySunHaloScale.value),
    uStarIntensity: makeNodeUniform(uniforms.uStarIntensity.value),
    uSunDir: makeNodeUniform(uniforms.uSunDir.value.clone()),
    uSecondarySunDir: makeNodeUniform(uniforms.uSecondarySunDir.value.clone()),
  };
}

function applyWebGPUGradientSkyUniforms(
  target: WebGPUGradientSkyUniforms,
  source: GradientSky['uniforms'],
): void {
  setSkyColorVector(target.uHorizonColor.value, source.uHorizonColor.value);
  setSkyColorVector(target.uLowerColor.value, source.uLowerColor.value);
  setSkyColorVector(target.uMidColor.value, source.uMidColor.value);
  setSkyColorVector(target.uUpperColor.value, source.uUpperColor.value);
  setSkyColorVector(target.uZenithColor.value, source.uZenithColor.value);
  setSkyColorVector(target.uOppositeHorizonColor.value, source.uOppositeHorizonColor.value);
  target.uOppositeHorizonStrength.value = source.uOppositeHorizonStrength.value;
  setSkyColorVector(target.uHorizonGlowColor.value, source.uHorizonGlowColor.value);
  target.uHorizonGlowIntensity.value = source.uHorizonGlowIntensity.value;
  setSkyColorVector(target.uSunColor.value, source.uSunColor.value);
  setSkyColorVector(target.uSecondarySunColor.value, source.uSecondarySunColor.value);
  target.uHorizonHueShift.value = source.uHorizonHueShift.value;
  target.uSunDiscScale.value = source.uSunDiscScale.value;
  target.uSunHaloScale.value = source.uSunHaloScale.value;
  target.uSecondarySunDiscScale.value = source.uSecondarySunDiscScale.value;
  target.uSecondarySunHaloScale.value = source.uSecondarySunHaloScale.value;
  target.uStarIntensity.value = source.uStarIntensity.value;
  target.uSunDir.value.copy(source.uSunDir.value);
  target.uSecondarySunDir.value.copy(source.uSecondarySunDir.value);
}

function skyColorVector(color: THREE.Color): THREE.Vector3 {
  return new THREE.Vector3(color.r, color.g, color.b);
}

function setSkyColorVector(target: THREE.Vector3, color: THREE.Color): void {
  target.set(color.r, color.g, color.b);
}
